import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { toast } from "react-toastify";
import { FaPlus, FaTrash, FaArrowUp, FaArrowDown } from "react-icons/fa";

const CHECK = (
  <svg className="w-4 h-4 text-primary" viewBox="0 0 20 20" fill="none">
    <polyline points="4,10 9,16 16,4" stroke="currentColor" strokeWidth="2.5" />
  </svg>
);

const DEFAULT_ROWS = [
  { label: "Enlèvement - chargement - transport - livraison et mise en place du mobilier à l'arrivée.", eco: true,  standard: true,  luxe: true  },
  { label: "Protection renforcée du mobilier sous couvertures.",                                        eco: true,  standard: true,  luxe: true  },
  { label: "Conditionnement des écrans et des meubles vitrés en papier bull-kraft.",                    eco: true,  standard: true,  luxe: true  },
  { label: "Protection de la literie sous housses.",                                                    eco: true,  standard: true,  luxe: true  },
  { label: "Mise en penderie des vêtements sur cintres.",                                               eco: true,  standard: true,  luxe: true  },
  { label: "Débranchement et branchement des appareils électroménagers.",                               eco: true,  standard: true,  luxe: true  },
  { label: "Livraison des fournitures : cartons standards, scotch et papier vaisselle.",                eco: true,  standard: true,  luxe: true  },
  { label: "Demande des autorisations de stationnement.",                                               eco: false, standard: true,  luxe: true  },
  { label: "Démontage et remontage des meubles (si nécessaire).",                                       eco: false, standard: true,  luxe: true  },
  { label: "Conditionnement des tableaux et miroirs en papier bull-kraft.",                             eco: false, standard: true,  luxe: true  },
  { label: "Emballage de la vaisselle et mise en cartons spéciaux.",                                    eco: false, standard: true,  luxe: true  },
  { label: "Emballage de l'ensemble des affaires fragiles et mise en cartons.",                         eco: false, standard: true,  luxe: true  },
  { label: "Empaquetage du linge, des livres et de l'ensemble des objets non fragiles.",                eco: false, standard: true,  luxe: true  },
  { label: "Déballage du linge, des livres et de l'ensemble des objets, fragiles ou non.",              eco: false, standard: false, luxe: true  },
  { label: "Débarras de tous les cartons vides, emballages et éléments superflus.",                     eco: false, standard: false, luxe: true  },
  { label: "Démontage des tringles à rideaux, accessoires et meubles muraux.",                          eco: false, standard: false, luxe: true  },
];

export default function TiersTab({ raw }) {
  const { t } = useTranslation();
  const [col1, setCol1] = useState(raw?.tiers_col1_name || "Éco");
  const [col2, setCol2] = useState(raw?.tiers_col2_name || "Standard");
  const [col3, setCol3] = useState(raw?.tiers_col3_name || "Luxe");
  const [rows, setRows] = useState(() => {
    try { return JSON.parse(raw?.tiers_config || "[]"); }
    catch { return DEFAULT_ROWS; }
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    try {
      const parsed = JSON.parse(raw?.tiers_config || "[]");
      setRows(parsed.length ? parsed : DEFAULT_ROWS);
    } catch { setRows(DEFAULT_ROWS); }
    setCol1(raw?.tiers_col1_name || "Éco");
    setCol2(raw?.tiers_col2_name || "Standard");
    setCol3(raw?.tiers_col3_name || "Luxe");
  }, [raw]);

  const updateRow = (i, field, value) =>
    setRows((r) => r.map((row, idx) => idx === i ? { ...row, [field]: value } : row));

  const addRow = () =>
    setRows((r) => [...r, { label: "", eco: true, standard: true, luxe: true }]);

  const removeRow = (i) =>
    setRows((r) => r.filter((_, idx) => idx !== i));

  const moveRow = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const copy = [...rows];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setRows(copy);
  };

  const resetToDefault = () => setRows(DEFAULT_ROWS);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        tiers_col1_name: col1,
        tiers_col2_name: col2,
        tiers_col3_name: col3,
        tiers_config: JSON.stringify(rows),
      });
      toast.success(t("settings.tiers.saved"));
    } catch {
      toast.error(t("settings.tiers.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const colHeaders = [col1, col2, col3];
  const colKeys    = ["eco", "standard", "luxe"];

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Column headers */}
      <div className="bg-base-200/50 rounded-xl p-4 border border-base-200">
        <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wider mb-3">
          {t("settings.tiers.colNames")}
        </p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { val: col1, set: setCol1, placeholder: "Éco" },
            { val: col2, set: setCol2, placeholder: "Standard" },
            { val: col3, set: setCol3, placeholder: "Luxe" },
          ].map(({ val, set, placeholder }, i) => (
            <div key={i} className="flex flex-col gap-1">
              <label className="text-xs font-medium text-base-content/60">{t("settings.tiers.column")} {i + 1}</label>
              <input
                type="text"
                value={val}
                onChange={(e) => set(e.target.value)}
                placeholder={placeholder}
                className="input input-bordered input-sm bg-base-100 text-base-content focus:border-primary focus:outline-none"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Rows table */}
      <div className="overflow-x-auto rounded-xl border border-base-200 bg-base-100 shadow-sm">
        <table className="table table-sm min-w-[700px]">
          <thead>
            <tr className="bg-base-200 border-b border-base-200">
              <th className="text-[11px] font-semibold uppercase tracking-wider text-base-content/60 w-8">#</th>
              <th className="text-[11px] font-semibold uppercase tracking-wider text-base-content/60">{t("settings.tiers.desc")}</th>
              {colHeaders.map((h, i) => (
                <th key={i} className="text-[11px] font-semibold uppercase tracking-wider text-base-content/60 text-center w-20">
                  {h}
                </th>
              ))}
              <th className="w-20" />
            </tr>
          </thead>
          <tbody className="divide-y divide-base-200">
            {rows.map((row, i) => (
              <tr key={i} className="group hover:bg-base-200/40 transition-colors">
                <td className="text-xs text-base-content/40 font-mono pl-3">{i + 1}</td>
                <td className="py-2 pr-3">
                  <input
                    type="text"
                    value={row.label}
                    onChange={(e) => updateRow(i, "label", e.target.value)}
                    className="w-full text-sm bg-transparent border-none outline-none focus:bg-base-200 focus:rounded px-1 py-0.5 text-base-content placeholder:text-base-content/30 transition-colors"
                    placeholder={t("settings.tiers.descPh")}
                  />
                </td>
                {colKeys.map((key) => (
                  <td key={key} className="text-center py-2">
                    <button
                      type="button"
                      onClick={() => updateRow(i, key, !row[key])}
                      className={`w-6 h-6 rounded border-2 flex items-center justify-center mx-auto transition-all ${
                        row[key]
                          ? "bg-primary border-primary"
                          : "border-base-300 hover:border-primary/50"
                      }`}
                      title={row[key] ? t("settings.tiers.exclude") : t("settings.tiers.include")}
                    >
                      {row[key] && (
                        <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="none">
                          <polyline points="4,10 9,16 16,4" stroke="currentColor" strokeWidth="2.5" />
                        </svg>
                      )}
                    </button>
                  </td>
                ))}
                <td className="py-2 pr-2">
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                    <button
                      type="button"
                      onClick={() => moveRow(i, -1)}
                      disabled={i === 0}
                      className="w-6 h-6 rounded flex items-center justify-center text-base-content/40 hover:text-base-content hover:bg-base-200 disabled:opacity-20 transition-colors"
                    >
                      <FaArrowUp size={9} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveRow(i, 1)}
                      disabled={i === rows.length - 1}
                      className="w-6 h-6 rounded flex items-center justify-center text-base-content/40 hover:text-base-content hover:bg-base-200 disabled:opacity-20 transition-colors"
                    >
                      <FaArrowDown size={9} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      className="w-6 h-6 rounded flex items-center justify-center text-base-content/40 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <FaTrash size={9} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-base-200 bg-base-100 text-base-content/70 text-sm font-medium hover:bg-base-200 transition-colors"
          >
            <FaPlus size={11} /> {t("settings.tiers.addRow")}
          </button>
          <button
            type="button"
            onClick={resetToDefault}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-base-200 bg-base-100 text-base-content/50 text-sm font-medium hover:bg-base-200 transition-colors"
          >
            {t("settings.tiers.reset")}
          </button>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn btn-primary btn-sm min-w-[120px]"
        >
          {saving ? <span className="loading loading-spinner loading-xs" /> : t("settings.tiers.save")}
        </button>
      </div>

      {/* Preview hint */}
      <p className="text-xs text-base-content/40">
        {t("settings.tiers.hint")}
      </p>
    </div>
  );
}
